import { BACKEND_URL } from "../config";
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const TradeHistoryDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 🔒 PRODUCTION ONLY - No localhost fallback
  const backendUrl = BACKEND_URL;

  useEffect(() => {
    fetchTradeStats();
  }, []);

  const fetchTradeStats = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${backendUrl}/api/trade-stats`);
      const data = await response.json();
      
      if (data.success) {
        setStats(data.stats);
        setError(null);
      } else {
        setError(data.message || 'Failed to fetch trade statistics');
      }
    } catch (err) {
      console.error('Error fetching trade stats:', err);
      setError('Unable to load trade statistics. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '$0.00';
    return `$${value.toFixed(2)}`;
  };

  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return '0h:0m:0s';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    return `${hours}h:${minutes}m:${secs}s`;
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)',
        padding: '2rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ color: 'white', fontSize: '1.5rem', fontWeight: '600' }}>
          Loading statistics...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)',
        padding: '2rem'
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '2rem',
            textAlign: 'center'
          }}>
            <div style={{ color: '#ef4444', fontSize: '1.25rem', marginBottom: '1rem' }}>
              ⚠️ {error}
            </div>
            <Link to="/profile" style={{
              padding: '0.75rem 1.5rem',
              background: '#0891b2',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '8px',
              fontWeight: '600',
              display: 'inline-block'
            }}>
              Back to Profile
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)',
      padding: '2rem'
    }}>
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '2rem',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <h1 style={{ color: 'white', fontSize: '2rem', fontWeight: '700' }}>
            📈 Advanced Statistics
          </h1>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <Link to="/" style={{
              padding: '0.75rem 1.5rem',
              background: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '8px',
              fontWeight: '600',
              border: '2px solid rgba(255,255,255,0.3)'
            }}>
              📊 Trade
            </Link>
            <Link to="/profile" style={{
              padding: '0.75rem 1.5rem',
              background: 'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '8px',
              fontWeight: '600',
              border: '2px solid rgba(255,255,255,0.3)'
            }}>
              👤 Profile
            </Link>
          </div>
        </div>

        {/* Stats Card */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '2rem',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
        }}>
          <h2 style={{ fontSize: '1.75rem', marginBottom: '2rem', color: '#0c4a6e' }}>
            Advanced Statistics
          </h2>

          {stats && stats.total_trades > 0 ? (
            <div style={{ display: 'grid', gap: '1rem' }}>
              {/* Total Trades */}
              <StatRow label="Total Trades" value={stats.total_trades} />
              
              {/* Profit/Loss Trades */}
              <StatRow label="Profit Trades" value={stats.profit_trades} />
              <StatRow label="Loss Trades" value={stats.loss_trades} />
              
              {/* Long/Short Trades */}
              <StatRow label="Long Trades" value={stats.long_trades} />
              <StatRow label="Short Trades" value={stats.short_trades} />
              
              {/* Financial Stats */}
              <StatRow label="Gross Profit" value={formatCurrency(stats.gross_profit)} valueColor="#16a34a" />
              <StatRow label="Gross Loss" value={formatCurrency(stats.gross_loss)} valueColor="#dc2626" />
              <StatRow 
                label="Total Net Profit" 
                value={formatCurrency(stats.net_profit)} 
                valueColor={stats.net_profit >= 0 ? '#16a34a' : '#dc2626'}
              />
              
              {/* Performance Metrics */}
              <StatRow label="Profit Factor" value={stats.profit_factor.toFixed(2)} />
              <StatRow label="Win Rate" value={`${stats.win_rate.toFixed(2)}%`} />
              
              {/* Best/Worst */}
              <StatRow label="Best Profit" value={formatCurrency(stats.best_profit)} valueColor="#16a34a" />
              <StatRow label="Biggest Loss" value={formatCurrency(stats.biggest_loss)} valueColor="#dc2626" />
              
              {/* Averages */}
              <StatRow label="Avg. Profit" value={formatCurrency(stats.avg_profit)} />
              <StatRow label="Avg. Loss" value={formatCurrency(stats.avg_loss)} />
              <StatRow 
                label="Expectancy" 
                value={formatCurrency(stats.expectancy)}
                valueColor={stats.expectancy >= 0 ? '#16a34a' : '#dc2626'}
              />
              
              {/* Trade Details */}
              <StatRow label="Avg. Trade Size" value={stats.avg_trade_size.toFixed(2)} />
              <StatRow label="Avg. Trade Duration" value={formatDuration(stats.avg_trade_duration)} />
            </div>
          ) : (
            <div style={{
              padding: '3rem',
              textAlign: 'center',
              color: '#64748b'
            }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📊</div>
              <div style={{ fontSize: '1.25rem', marginBottom: '0.5rem' }}>No trading data yet</div>
              <div style={{ fontSize: '1rem' }}>Start trading to see your advanced statistics here</div>
              <Link to="/" style={{
                marginTop: '1.5rem',
                padding: '0.75rem 1.5rem',
                background: '#0891b2',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '8px',
                fontWeight: '600',
                display: 'inline-block'
              }}>
                Start Trading
              </Link>
            </div>
          )}
        </div>

        {/* Info Banner */}
        <div style={{
          marginTop: '1.5rem',
          padding: '1rem',
          background: 'rgba(255, 255, 255, 0.2)',
          borderRadius: '12px',
          color: 'white',
          fontSize: '0.875rem',
          textAlign: 'center'
        }}>
          Statistics are calculated from closed trades in the last 30 days
        </div>
      </div>
    </div>
  );
};

// Helper component for stat rows
const StatRow = ({ label, value, valueColor = '#0c4a6e' }) => (
  <div style={{
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.75rem 0',
    borderBottom: '1px solid #e2e8f0'
  }}>
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      color: '#64748b',
      fontSize: '1rem'
    }}>
      {label}
      <span style={{
        display: 'inline-block',
        width: '16px',
        height: '16px',
        borderRadius: '50%',
        background: '#e2e8f0',
        color: '#94a3b8',
        fontSize: '0.75rem',
        textAlign: 'center',
        lineHeight: '16px',
        cursor: 'help'
      }}>?</span>
    </div>
    <div style={{
      fontSize: '1.25rem',
      fontWeight: '700',
      color: valueColor
    }}>
      {value}
    </div>
  </div>
);

export default TradeHistoryDashboard;
